import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// Pages
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import EmailVerificationPage from './pages/EmailVerificationPage'
import PasswordVerifyPage from './pages/PasswordVerifyPage'
import DashboardPage from './pages/DashboardPage'
import BookmarkPage from './pages/BookmarkPage'
import AnalyticsPage from './pages/AnalyticsPage'
import LinkAnalyticsPage from './pages/LinkAnalyticsPage'
import SettingsPage from './pages/SettingsPage'
import ProfilePage from './pages/ProfilePage'
import PricingPage from './pages/PricingPage'
import TeamCollaborationPage from './pages/TeamCollaborationPage'
import TeamJoinPage from './pages/TeamJoinPage'
import NotFoundPage from '../../shared/pages/NotFoundPage'

// Components
import Header from '../../shared/components/layout/Header'
import Footer from '../../shared/components/layout/Footer'
import ProtectedRoute from '../../shared/components/ProtectedRoute'

// Hooks
import { useTheme } from '../../shared/hooks/useTheme'
import { useDocumentLanguage } from '../../shared/hooks/useDocumentLanguage'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

function UserApp() {
  useDocumentLanguage()
  const { resolvedTheme, initializeTheme } = useTheme()

  useEffect(() => {
    initializeTheme()
  }, [initializeTheme])

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="min-h-screen bg-gray-50 transition-colors duration-200 dark:bg-gray-900">
          <Routes>
            {/* Auth routes - no header/footer, redirect if authenticated */}
            <Route
              path="/login"
              element={
                <ProtectedRoute requireAuth={false}>
                  <LoginPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/register"
              element={
                <ProtectedRoute requireAuth={false}>
                  <RegisterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/verify-email"
              element={
                <ProtectedRoute requireAuth={false}>
                  <EmailVerificationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <ProtectedRoute requireAuth={false}>
                  <LoginPage />
                </ProtectedRoute>
              }
            />

            {/* Public Password Verification page - no auth required */}
            <Route path="/:shortCode/verify" element={<PasswordVerifyPage />} />

            {/* Team join page - no auth required */}
            <Route path="/team/join/:token" element={<TeamJoinPage />} />

            {/* Public Pricing page - no auth required */}
            <Route
              path="/price"
              element={
                <div className="min-h-screen bg-gray-50 transition-colors duration-200 dark:bg-gray-900">
                  <Header />
                  <main className="flex-1">
                    <PricingPage />
                  </main>
                  <Footer />
                </div>
              }
            />

            {/* Protected app routes - with header/footer */}
            <Route
              path="/*"
              element={
                <ProtectedRoute requireAuth={true}>
                  <Header />
                  <main className="flex-1">
                    <Routes>
                      <Route path="/" element={<DashboardPage />} />
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route path="/bookmarks" element={<BookmarkPage />} />
                      <Route
                        path="/team-collaboration"
                        element={<TeamCollaborationPage />}
                      />
                      <Route path="/analytics" element={<AnalyticsPage />} />
                      <Route
                        path="/analytics/:shortCode"
                        element={<LinkAnalyticsPage />}
                      />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/settings" element={<SettingsPage />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </main>
                  <Footer />
                </ProtectedRoute>
              }
            />
          </Routes>

          {/* Toast Notifications */}
          <Toaster
            position="bottom-right"
            reverseOrder={false}
            gutter={8}
            containerClassName=""
            containerStyle={{}}
            toastOptions={{
              className: '',
              duration: 4000,
              style: {
                background: resolvedTheme === 'dark' ? '#374151' : '#ffffff',
                color: resolvedTheme === 'dark' ? '#f9fafb' : '#111827',
                border:
                  resolvedTheme === 'dark'
                    ? '1px solid #4b5563'
                    : '1px solid #e5e7eb',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#ffffff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#ffffff',
                },
              },
            }}
          />
        </div>
      </Router>
    </QueryClientProvider>
  )
}

export default UserApp
